import { AbsProduct } from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import getProductAbbreviationByPath from '@/creditflowresearch/infrastructure/productRouting';
import { fromUnixTime, format } from 'date-fns';
import { validateUrlFilter } from '@/creditflowresearch/application/PageFiltersState/productRegionFilters/validateUrlFilter';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';
import * as FILTER_TYPES from '@/creditflowresearch/application/PageFiltersState/constants';
import { YEAR_FORMAT } from '@dealroadshow/uikit-date-time/dateFormats';
import downloadFile from '@framework/api/downloadFile';
import IDeal from '@/creditflowresearch/domain/vo/IDeal';
import { ICorporateDeal } from '@/creditflowresearch/domain/vo/Deals/CorporateDeal';
import type { TServerData } from '@/creditflowresearch/application/common/serverSide/types';
import getServerSideSectorsIndustries from '@/creditflowresearch/application/HeaderMenu/serverSide/getServerSideSectorsIndustries';

export const isServerError = <T>(data: TServerData<T>): data is { hasServerError: boolean } =>
  data !== null && typeof data === 'object' && 'hasServerError' in data;

export const getValidChangedProductsRegions = ({
  products,
  productsList,
  regions,
  regionsList,
  pathname,
}) => {
  const {
    validKeys: validProducts,
    keysChanged: productsChanged,
  } = getValidChanged(
      products,
      productsList,
      getProductAbbreviationByPath(pathname),
  );

  const {
    validKeys: validRegions,
    keysChanged: regionsChanged,
  } = getValidChanged(
      regions,
      regionsList,
      Region.getDefaultAbbreviation(),
  );

  return {
    validProducts,
    productsChanged,
    validRegions,
    regionsChanged,
  };
};

export const getValidChanged = (urlKeys, collection, defaultValue) => {
  const {
    updatedQuery: validKeys,
    hasChanged: keysChanged,
  } = validateUrlFilter(urlKeys, collection, defaultValue);

  return {
    validKeys,
    keysChanged,
  };
};

export const insertCompanyFilters = async (context, filter) => {
  const { query } = context;
  const {
    sponsorName,
    issuerName,
  } = query;

  const issuer = context?.company;
  if (sponsorName) filter.includedParentIds = [issuer?.id];
  if (issuerName) filter.includedIssuerIds = [issuer.id];

  return filter;
};

export const insertSectorFilters = (context, filter) => {
  const { query } = context;
  const {
    sector,
  } = query;
  const sectorItem = getServerSideSectorsIndustries(context).sectorsList[sector];

  if (sectorItem.sectorId) {
    filter.includedSectorId = sectorItem.sectorId;
  }
  if (sectorItem.subsectorId) {
    filter.includedSubsectorId = sectorItem.subsectorId;
  }
  return filter;
};

export const insertIndustryFilters = (context, filter) => {
  const { query } = context;
  const {
    industry,
  } = query;

  if (industry) {
    const industryItem = getServerSideSectorsIndustries(context).industriesList[industry];

    if (industryItem.industryId) {
      filter.includedIndustryId = industryItem.industryId;
    }
    if (industryItem.subindustryId) {
      filter.includedSubindustryId = industryItem.subindustryId;
    }
  }

  return filter;
};

// TODO: remove after create global redirect
export const isChangedProductsRegions = (
  regions: string = '', // params from url
  products: string = '', // params from url
  validRegions: string[], // array of abbreviation
  validProducts: string[], // array of abbreviation
) => {
  const sortRegions = sortBy(regions.split(','));
  const sortProducts = sortBy(products.split(','));
  const sortValidRegions = sortBy(validRegions);
  const sortValidProducts = sortBy(validProducts);

  return !isEqual(sortRegions, sortValidRegions) || !isEqual(sortProducts, sortValidProducts);
};

interface IAdditionalParams {
  dateFrom?: number,
  dateTo?: number,
}

export const getAdditionalParams = (filtersPayload: IAdditionalParams):IAdditionalParams => {
  const additionalParams:IAdditionalParams = {};

  if (filtersPayload.dateFrom) {
    additionalParams.dateFrom = filtersPayload.dateFrom;
  }

  if (filtersPayload.dateTo) {
    additionalParams.dateTo = filtersPayload.dateTo;
  }

  return additionalParams;
};

export const getFilterFromSector = (sectorItem) => {
  if (sectorItem.subsectorId) {
    return { includedSubsectorIds: [sectorItem.subsectorId] };
  }

  return { includedSectorIds: [sectorItem.sectorId] };
};

export const getFilterFromIndustry = (industryItem) => {
  if (industryItem.subindustryId) {
    return { includedSubindustryIds: [industryItem.subindustryId] };
  }

  return { includedIndustryIds: [industryItem.industryId] };
};

export const getSectorFilters = (context) => {
  const { query: { sector } } = context;
  const sectorItem = getServerSideSectorsIndustries(context).sectorsList[sector];

  return getFilterFromSector(sectorItem);
};

export const getIndustryFilters = (context) => {
  const { query: { industry } } = context;

  const industryItem = getServerSideSectorsIndustries(context).industriesList[industry];

  return getFilterFromIndustry(industryItem);
};

export const getMarketChartFilterType = (context) => {
  const { productsRegions: { productIds } } = context;

  return productIds.includes(AbsProduct.PRODUCT_ID)
      ? FILTER_TYPES.SECTOR
      : FILTER_TYPES.INDUSTRY;
};

export const toYearsFormatter = (value) => (value ? format(fromUnixTime(value), YEAR_FORMAT) : null);

export const getUrlAndDownloadAttachment = async (requestMethod, { uploadId, displayName }) => {
    const { url } = await requestMethod({ uploadId, displayName });
    downloadFile(url);
};

interface IProductsRegionsIds {
  productIds: string[],
  regionIds: string[],
}

export const getRegionAndProductFromDeal = (
  deal: IDeal | ICorporateDeal | null,
  defaultValues: IProductsRegionsIds,
): IProductsRegionsIds => {
  if (!deal) return defaultValues;

  return {
    productIds: [deal.product.id],
    regionIds: [deal.country.region.id],
  };
};
